<template>
  <b-modal id="modal-dialog" ref="modal-dialog" size="xl" :title="modalTitle" @hidden="clearModalData">
    <!-- Modal body for all modals -->
    <p class="my-4" v-if="this.modalBody && this.modalBody.length > 0">{{ this.modalBody }}</p>
    <!-- regular input modal -->
    <b-input v-if="inputPlaceholder" v-model="inputValue" :placeholder="inputPlaceholder"></b-input>
    <!-- file preview modal -->
    <div v-if="filePreview">
      <div v-if="filePreview.type === 'image' && filePreview.mime_type.indexOf('image') === 0">
        <b-img :src="filePreview.file_url" fluid></b-img>
      </div>
      <div v-if="filePreview.type === 'video' && filePreview.mime_type.indexOf('video') === 0">
        <b-embed type="video" controls>
          <source :src="filePreview.file_url" :type="filePreview.mime_type">
        </b-embed>
      </div>
      <div v-if="filePreview.type === 'url'">
        <b-embed type="iframe" :src="filePreview.path">
        </b-embed>
      </div>
    </div>
    <!-- file upload modal -->
    <b-row v-if="fileUpload">
      <b-col cols="12">
        <b-form-file multiple v-model="filesArray" :file-name-formatter="formatNames" accept="image/*"></b-form-file>
        <ul class="mt-2">
          <li class="file-item" v-for="(file, index) in filesArray" :key="index">
            {{ file.name }}
          </li>
        </ul>
      </b-col>
    </b-row>
    <!-- video upload modal -->
    <b-row v-if="videoUpload">
      <b-col cols="12">
        <b-form-file multiple v-model="filesArray" :file-name-formatter="formatNames" accept="video/*"></b-form-file>
        <ul class="mt-2">
          <li class="file-item" v-for="(file, index) in filesArray" :key="index">
            {{ file.name }}
          </li>
        </ul>
      </b-col>
    </b-row>
    <!-- url upload modal -->
    <b-row v-if="urlUpload">
      <b-col cols="12">
        <b-form-group>
          <b-form-input v-model="url" required placeholder="URL"/>
        </b-form-group>
      </b-col>
    </b-row>
    <!-- file picker modal -->
    <MediaViewer
      v-if="filePicker"
      :targetPath="pickerPath"
      @route-update="pickerPath = $event.params.path"
      :isPicker="true"
      :multiPick="true"
      :pickerFoldersOnly="pickerFolders"
      :pickedFilesArray="pickedFilesArray"
      :hideFolder="folderIgnoreId"
      :view="'list'"
      :orientation="orientation"
      @current-folder-update="pickerFolderValue = $event.id"
      @file-clicked="selectFile"/>

    <!-- device picker modal -->
    <DevicesViewer
      v-if="devicePicker"
      :picker-mode="true"
      :filter-array="selectedFiles"
      @item-clicked="addDevice"/>
    <!-- playlist picker modal -->
    <PlaylistsViewer
      v-if="playlistPicker"
      :picker-mode="true"
      :playlist-type="playlistType"
      :orientation="orientation"
      @item-clicked="selectPlaylist"/>
    <!-- modal buttons -->
    <div slot="modal-footer" class="w-100">
        <b-button variant="primary" v-if="confirmText" class="float-right mx-2" @click="modalConfirm">{{ confirmText }}</b-button>
        <b-button variant="light" v-if="cancelText" class="float-right mx-2" @click="modalCancel">{{ cancelText }}</b-button>
    </div>
  </b-modal>
</template>

<script>
import MediaViewer from '@/components/MediaViewer.vue';
import DevicesViewer from '@/components/DevicesViewer.vue';
import PlaylistsViewer from '@/components/PlaylistsViewer.vue';

export default {
  components: { MediaViewer, DevicesViewer, PlaylistsViewer },
  data() {
    return {
      modalTitle: '',
      modalBody: '',
      modalCbConfirm: null,
      modalCbCancel: null,
      confirmText: '',
      cancelText: '',
      inputValue: '',
      inputPlaceholder: null,
      fileUpload: false,
      videoUpload: false,
      urlUpload: false,
      filesArray: [],
      url: '',
      filePicker: false,
      pickerFolders: false,
      pickerPath: '',
      pickerFolderValue: null,
      folderIgnoreId: null,
      devicePicker: false,
      devicesArray: [],
      devicesFilterArray: [],
      playlistPicker: false,
      playlistType: null,
      orientation: null,
      filePreview: null,
      multiPick: true,
      pickedFilesArray: [],
    };
  },
  created() {
    this.$root.$on('modal-show', this.showModal);
  },
  beforeDestroy() {
    this.$root.$off('modal-show', this.showModal);
  },
  methods: {
    showModal({
      title,
      body,
      cbConfirm,
      cbCancel,
      confirmText,
      cancelText,
      inputPlaceholder,
      fileUpload,
      videoUpload,
      urlUpload,
      filePicker,
      pickerFolders,
      folderIgnoreId,
      devicePicker,
      devicesFilterArray,
      playlistPicker,
      playlistType,
      orientation,
      filePreview,
      multiPick,
    }) {
      this.modalTitle = title;
      this.modalBody = body;
      this.modalCbConfirm = cbConfirm || null;
      this.modalCbCancel = cbCancel || null;
      this.confirmText = confirmText;
      this.cancelText = cancelText;
      this.inputPlaceholder = inputPlaceholder;
      this.inputValue = '';
      this.fileUpload = fileUpload;
      this.videoUpload = videoUpload;
      this.urlUpload = urlUpload;
      this.filesArray = [];
      this.url = '';
      this.filePicker = filePicker;
      this.pickerFolders = pickerFolders;
      this.pickerPath = '';
      this.pickerFolderValue = null;
      this.folderIgnoreId = folderIgnoreId;
      this.devicePicker = devicePicker;
      this.devicesArray = [];
      this.devicesFilterArray = devicesFilterArray ? devicesFilterArray.map(e => ({ ...e, unavailable: true })) : [];
      this.playlistPicker = playlistPicker;
      this.playlistType = playlistType;
      this.orientation = orientation; // 0 - landscape, 1 - portrait
      this.filePreview = filePreview;
      this.multiPick = multiPick;
      this.pickedFilesArray = [];
      this.$refs['modal-dialog'].show();
    },
    hideModal() {
      this.$refs['modal-dialog'].hide();
    },
    clearModalData() {
      this.modalTitle = null;
      this.modalBody = null;
      this.modalCbConfirm = null;
      this.modalCbCancel = null;
      this.confirmText = null;
      this.cancelText = null;
      this.inputPlaceholder = null;
      this.inputValue = null;
      this.fileUpload = null;
      this.videoUpload = null;
      this.urlUpload = null;
      this.filesArray = null;
      this.url = null;
      this.filePicker = null;
      this.pickerFolders = null;
      this.pickerPath = '';
      this.pickerFolderValue = null;
      this.folderIgnoreId = null;
      this.devicePicker = null;
      this.devicesArray = [];
      this.devicesFilterArray = [];
      this.playlistPicker = null;
      this.playlistType = null;
      this.orientation = null;
      this.filePreview = null;
      this.pickedFilesArray = [];
    },
    modalConfirm(data) {
      if (this.modalCbConfirm) {
        if (this.inputValue) this.modalCbConfirm(this.inputValue);
        else if (this.fileUpload) this.modalCbConfirm(this.filesArray);
        else if (this.videoUpload) this.modalCbConfirm(this.filesArray);
        else if (this.urlUpload) this.modalCbConfirm(this.url);
        else if (this.filePicker && this.pickerFolders) this.modalCbConfirm(this.pickerFolderValue);
        else if (this.filePicker && !this.pickerFolders) this.modalCbConfirm(this.pickedFilesArray);
        else if (this.devicePicker) this.modalCbConfirm(this.devicesArray);
        else if (this.playlistPicker) this.modalCbConfirm(data);
        else this.modalCbConfirm(data);
      }
      this.hideModal();
    },
    modalCancel() {
      if (this.modalCbCancel) this.modalCbCancel();
      this.hideModal();
    },
    formatNames(files) {
      if (files.length === 1) {
        return files[0].name;
      }

      return `${files.length} files selected`;
    },
    selectFile($event) {
      if (!this.pickedFilesArray.find(e => e.id === $event.id)) {
        this.pickedFilesArray.push($event);
      } else {
        this.pickedFilesArray = this.pickedFilesArray.filter(e => e.id !== $event.id);
      }
      if (!this.multiPick) {
        this.pickedFilesArray.push($event);
        this.modalConfirm();
      }
    },
    selectPlaylist($event) {
      this.modalConfirm($event);
    },
    addDevice(d) {
      const foundInFilter = this.devicesFilterArray.find(e => e.id === d.id);
      const foundInExisting = this.devicesArray.find(e => e.id === d.id);
      if (!foundInFilter && !foundInExisting) {
        this.devicesArray.push(d);
      } else if (foundInFilter) {
        this.$root.$emit('notification-add', { content: 'Device already selected.', variant: 'danger' });
      } else if (foundInExisting) {
        this.devicesArray = this.devicesArray.filter(e => e !== foundInExisting);
      }
    },
  },
  computed: {
    selectedFiles() {
      return [...this.devicesArray, ...this.devicesFilterArray];
    },
  },
};
</script>
